import type * as React from 'react'
import { createContext, useContext, useMemo, useState } from 'react'
import type { JSX } from 'react'

import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import type {
  DragCancelEvent,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
} from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'

import { ChannelAvatar, Flex, Sidebar, Text } from '@buffer-mono/popcorn'

import { getChannelAdditionalInfo } from '~publish/helpers/channels'
import { getChannelNameForDisplay } from '~publish/helpers/getChannelNameForDisplay'

import type { MainSidebarChannel } from '../useChannels'

import styles from './DraggableChannelsProvider.module.css'

interface DraggableChannelsContextValue {
  isDragging: boolean
}

const DraggableChannelsContext = createContext<
  DraggableChannelsContextValue | undefined
>(undefined)

/**
 * Hook to access the dragging state from DraggableChannelsProvider.
 * Must be used within a DraggableChannelsProvider component.
 */
export function useDraggableChannelsContext(): DraggableChannelsContextValue {
  const context = useContext(DraggableChannelsContext)
  if (!context) {
    throw new Error(
      'useDraggableChannelsContext must be used within DraggableChannelsProvider',
    )
  }
  return context
}

export const DraggableChannelsProvider = ({
  disabled,
  children,
  channels,
  onReorder,
  onDragStart,
}: {
  disabled: boolean
  children: React.ReactNode
  channels: MainSidebarChannel[]
  onReorder: (dragIndex: number, hoverIndex: number) => void | Promise<void>
  onDragStart?: (channelId: string) => void
}): JSX.Element => {
  const [draggedChannel, setDraggedChannel] = useState<
    MainSidebarChannel | undefined
  >(undefined)

  const isDragging = !!draggedChannel

  const handleDragStart = (event: DragStartEvent): void => {
    const { active } = event
    if (!active.id) return
    const channel = channels.find(
      (candidateChannel) => candidateChannel.id === active.id,
    )
    setDraggedChannel(channel)

    // Notify parent that drag has started
    if (channel && onDragStart) {
      onDragStart(channel.id)
    }
  }

  const handleDragEnd = ({ active, over }: DragEndEvent): void => {
    setDraggedChannel(undefined)
    if (!active.id || !over?.id) return

    const dragIndex = channels.findIndex((channel) => channel.id === active.id)
    const hoverIndex = channels.findIndex((channel) => channel.id === over.id)

    // Guard against invalid indexes or no-op drags
    if (dragIndex === -1 || hoverIndex === -1 || dragIndex === hoverIndex) {
      return
    }

    // Assumes onReorder handles its own errors (including async rejections)
    void onReorder(dragIndex, hoverIndex)
  }

  const handleDragCancel = (): void => {
    setDraggedChannel(undefined)
  }

  const sensors = useSensors(
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Prevent accidental drags
      },
    }),
  )

  const getPosition = (id: string): number =>
    channels.findIndex((channel) => channel.id === id) + 1
  const channelCount = channels.length

  const getChannelInfo = (id: string): string => {
    const channel = channels.find((c) => c.id === id)
    return channel
      ? `${getChannelNameForDisplay(channel)} from ${channel.service}`
      : 'Unknown channel'
  }

  const contextValue = useMemo(() => ({ isDragging }), [isDragging])

  const announcements = {
    onDragStart({ active }: DragStartEvent): string {
      const channelInfo = getChannelInfo(active.id.toString())
      return `Picked up channel ${channelInfo}. Channel is in position ${getPosition(
        active.id.toString(),
      )} of ${channelCount}`
    },
    onDragOver({ active, over }: DragOverEvent): string {
      if (over) {
        const channelInfo = getChannelInfo(active.id.toString())
        return `Channel ${channelInfo} was moved into position ${getPosition(
          over.id.toString(),
        )} of ${channelCount}`
      }
      return `Channel ${getChannelInfo(
        active.id.toString(),
      )} is no longer over a droppable area`
    },
    onDragEnd({ active, over }: DragEndEvent): string {
      if (over) {
        const channelInfo = getChannelInfo(active.id.toString())
        return `Channel ${channelInfo} was dropped at position ${getPosition(
          over.id.toString(),
        )} of ${channelCount}`
      }
      return `Channel ${getChannelInfo(active.id.toString())} was dropped`
    },
    onDragCancel({ active }: DragCancelEvent): string {
      return `Dragging was cancelled. Channel ${getChannelInfo(
        active.id.toString(),
      )} was dropped.`
    },
  }

  return (
    <DraggableChannelsContext.Provider value={contextValue}>
      <DndContext
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        sensors={sensors}
        modifiers={[restrictToFirstScrollableAncestor]}
        accessibility={{
          announcements,
          screenReaderInstructions: {
            draggable: `
            To pick up a channel, press space or enter.
            Use the up and down arrow keys to change the channel's position in the list.
            Press space or enter again to drop the channel in its new position, or press escape to cancel.
          `,
          },
        }}
      >
        <SortableContext
          items={channels}
          strategy={verticalListSortingStrategy}
          disabled={disabled}
        >
          {children}
        </SortableContext>
        <DragOverlay>
          {draggedChannel && <DragOverlayItem channel={draggedChannel} />}
        </DragOverlay>
      </DndContext>
    </DraggableChannelsContext.Provider>
  )
}

/**
 * Simplified channel item component for the drag overlay.
 * This renders a non-interactive version of the channel for visual feedback during drag.
 */
function DragOverlayItem({
  channel,
}: {
  channel: MainSidebarChannel
}): JSX.Element {
  const additionalInfo = getChannelAdditionalInfo(channel)

  return (
    <div className={styles.dragOverlayItem}>
      <Sidebar.Button
        as="div"
        prefix={
          <ChannelAvatar
            alt={channel.name}
            channel={channel.service}
            src={channel.avatar}
            size="xsmall"
            className={styles.dragOverlayAvatar}
          />
        }
      >
        <Flex direction="column">
          <Text>{getChannelNameForDisplay(channel)}</Text>
          {additionalInfo && (
            <Text color="subtle" size="sm">
              {additionalInfo}
            </Text>
          )}
        </Flex>
      </Sidebar.Button>
    </div>
  )
}
